import { Link } from "react-router-dom";
import CyberBrand from "../../components/common/CyberBrand";
import { scrollToSection } from "./scrollToSection";

const TREE = [
  {
    dir: "platform",
    links: [
      { label: "capabilities", href: "#features" },
      { label: "cyber-range", href: "#cyber-range" },
      { label: "labs", href: "#labs" },
      { label: "dashboards", href: "#dashboards" },
      { label: "certification", href: "#tksa" },
    ],
  },
  {
    dir: "ai",
    links: [
      { label: "ai-copilots", href: "#ai" },
      { label: "topology-designer", href: "#topology" },
    ],
  },
  {
    dir: "solutions",
    links: [
      { label: "universities", href: "/university/login" },
      { label: "enterprises", href: "/company/login" },
      { label: "individuals", href: "/register" },
    ],
  },
  {
    dir: "get-started",
    links: [
      { label: "create-account", href: "/register" },
      { label: "sign-in", href: "/login" },
      { label: "platform-admin", href: "/platform/login" },
    ],
  },
];

/* One line of `tree` output. In-page anchors glide to their section; everything
 * else is a real route, so it stays a router Link. */
function TreeLeaf({ href, label, isLast }) {
  const className =
    "group flex items-center gap-2 py-0.5 font-mono text-[12.5px] text-(--color-text-muted) transition-[color,transform] duration-200 hover:translate-x-0.5 hover:text-(--color-primary)";

  const body = (
    <>
      <span className="opacity-50 transition-[color,opacity] duration-200 group-hover:opacity-100 group-hover:text-(--color-primary)">
        {isLast ? "└──" : "├──"}
      </span>
      {label}
    </>
  );

  if (!href.startsWith("#")) {
    return (
      <Link to={href} className={className}>
        {body}
      </Link>
    );
  }

  return (
    <a
      href={href}
      onClick={(e) => {
        e.preventDefault();
        e.currentTarget.blur();
        scrollToSection(href);
      }}
      className={className}
    >
      {body}
    </a>
  );
}

export default function LandingFooter() {
  const year = new Date().getFullYear();

  return (
    <footer
      dir="ltr"
      className="relative z-10 border-t border-(--color-border) bg-(--color-bg-overlay-80) backdrop-blur-xl"
    >
      <div className="mx-auto max-w-350 px-5 pt-12 md:px-10">
        {/* The command whose output is the sitemap below. */}
        <p className="mb-9 font-mono text-[13px] text-(--color-text-secondary)">
          <span className="text-(--color-primary)">qv@vanguard</span>
          <span className="text-(--color-text-muted)">:~</span>
          <span className="mx-1.5 text-(--color-text-muted)">$</span>
          tree ./quantum-vanguard --depth 1
          <span className="lp-caret ml-1.5 inline-block h-3.5 w-1.75 translate-y-0.5 bg-(--color-primary)" />
        </p>

        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_repeat(4,1fr)]">
          <div className="max-w-xs">
            <CyberBrand size="md" showSubtitle={false} />
            <p className="mt-4 font-mono text-[12px] leading-relaxed text-(--color-text-muted)">
              The all-in-one cyber range platform for hands-on offensive and
              defensive security training.
            </p>
          </div>

          {TREE.map((branch) => (
            <div key={branch.dir}>
              <h4 className="mb-3 font-mono text-[13px] text-(--color-text-primary)">
                <span className="text-(--color-text-muted) opacity-60">./</span>
                {branch.dir}
              </h4>
              <div className="flex flex-col">
                {branch.links.map((link, i) => (
                  <TreeLeaf
                    key={link.label}
                    {...link}
                    isLast={i === branch.links.length - 1}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status line — a tmux/vim bar, full-bleed to the viewport edges. */}
      <div className="mt-12 flex flex-wrap items-stretch border-t border-(--color-border-solid) bg-(--color-bg-card) font-mono text-[11px]">
        <span className="flex items-center border-r border-(--color-border) px-3.5 py-2.5 tracking-[0.04em] text-(--color-text-muted)">
          © {year} · all rights reserved
        </span>
        <span className="ml-auto flex items-center gap-5 border-l border-(--color-border) px-3.5 py-2.5">
          <a
            href="#"
            className="text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
          >
            terms
          </a>
          <a
            href="#"
            className="text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
          >
            privacy
          </a>
        </span>
      </div>
    </footer>
  );
}
